import {
  Box,
  HStack,
  Divider,
  Stack,
  Text,
  Flex,
  Tooltip,
  Center,
  CenterProps,
  useDisclosure,
  IconButton,
} from '@chakra-ui/react'
import React from 'react'
import { BlankSlate } from 'components/skeletons/skeleton-unit'
import { Card } from 'components/card/card'
import { useProperty } from 'api/property'
import { UpdatePropertyModal } from 'features/property/add-new-property-modal'
import { useStates } from 'api/pc-projects'
import { truncateWithEllipsis } from 'utils/string-formatters'
import { PropertyEditIcon } from 'icons/work-order-filter-icons'
import { isValidAndNonEmpty } from 'utils'
import { MdPhone } from 'react-icons/md'
import { useContactCenterModalEvents, useSelectedResidentContact } from 'components/contact-center'
import { BiCaretLeft, BiCaretRight } from 'react-icons/bi'
import Client from 'features/clients/selected-client-modal'
import { useUserRolesSelector } from 'utils/redux-common-selectors'

type Props = {
  propertyId: string
  projectId?: number | null | string
}

export const ResidentBox: React.FC<Props> = ({ propertyId, projectId }: Props) => {
  const { propertyData, isLoading: isLoadingPropertyData } = useProperty(propertyId, true)
  const { states, isLoading: isLoadingStatesData } = useStates()
  const [isHovered, setIsHovered] = React.useState<boolean>(false)
  const [isLeftDirection, setIsLeftDirection] = React.useState<boolean>(false); // HN-PSWOA-6031| This state will change the direction and logic of slider
  const [isClientBoxHovered, setIsClientBoxHovered] = React.useState<boolean>(false);
  const { isOpen, onOpen, onClose: onCloseDisclosure } = useDisclosure()

  const [selectedResident, setSelectedResident] = React.useState<any>(null)
  const { open: openContactCener } = useContactCenterModalEvents()
  const { setSelected: setContactCenterSelectedContact } = useSelectedResidentContact()

  React.useEffect(() => {
    if (!propertyData?.residents) return
    setSelectedResident(propertyData?.residents[0])
  }, [propertyData?.residents])

  const {
    isOpen: isOpenUpdatePropertyModal,
    onClose: onUpdatePropertyModalClose,
    onOpen: onUpdatePropertyModalOpen,
  } = useDisclosure()

  const editProperty = () => {
    onUpdatePropertyModalOpen()
  }

  const nextResident = () => {
    const residents = propertyData.residents

    if (residents?.findIndex(r => r.id === selectedResident?.id) === residents?.length - 1) {
      prevResident();
      return
    }

    if (residents?.findIndex(r => r.id === selectedResident?.id) === residents?.length - 2) setIsLeftDirection(true);
    setSelectedResident(residents[residents?.findIndex(r => r.id === selectedResident.id) + 1])
  }

  const prevResident = () => {
    const residents = propertyData.residents

    if (residents?.findIndex(r => r.id === selectedResident?.id) === 0) {
      setIsLeftDirection(false);
      nextResident();
      return
    }

    if (residents?.findIndex(r => r.id === selectedResident?.id) === 1) setIsLeftDirection(false);
    setSelectedResident(residents[residents?.findIndex(r => r.id === selectedResident.id) - 1])
  }

  const handleCall = (contact: any) => {
    setContactCenterSelectedContact(contact)
    openContactCener()
  }
  const { isAdmin, isAccounting, isFPM: isMaintenanceSpecialist , isProjectCoordinator: isMaintenanceCoordinator   } = useUserRolesSelector()
  const showCallModule = !window.location.href.includes('preprod') && ( isAdmin || isAccounting || isMaintenanceSpecialist || isMaintenanceCoordinator )
  return (
    <>
      <Card
        background="#FDFDFF"
        border="1px solid #EBEBEB"
        borderRadius="6px"
        p="10px"
        boxShadow="0px 4px 6px -1px rgb(0 0 0 / 10%), 0px 2px 4px -1px rgb(0 0 0 / 6%)"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {propertyData && states && (
          <Stack>
            <Box>
              <Flex flexWrap="wrap" w="100%" bg="white" borderRadius="6px" mt={{ base: 5, sm: 0 }} pos="relative">
                {isHovered && (
                  <IconButton
                    aria-label={'Edit Property'}
                    icon={<PropertyEditIcon />}
                    onClick={editProperty}
                    left="97%"
                    top="-8px"
                    position="absolute"
                    bg="transparent !important"
                    justifyContent="start"
                    minWidth="32px !important"
                    width="32px !important"
                    h="32px !important"
                    zIndex="9999"
                  />
                )}
                {isClientBoxHovered && (
                    <IconButton
                      aria-label={"Edit Client"}
                      icon={<PropertyEditIcon />}
                      onClick={onOpen}
                      left="97%"
                      top="130%"
                      position="absolute"
                      bg="transparent !important"
                      justifyContent="start"
                      minWidth="32px !important"
                      width="32px !important"
                      h="32px !important"
                      zIndex="9999"
                      onMouseEnter={() => setIsClientBoxHovered(true)} 
                      onMouseLeave={() => setIsClientBoxHovered(false)}
                    />
                  )}
                <InfoStructureCard
                  tooltipTitle={propertyData.streetAddress}
                  isLoading={false}
                  borderLeft="none"
                  justifyContent="start"
                  width="160px"
                  sx={{
                    '@media only screen and (max-width: 407px)': {
                      borderRight: '1px solid #CBD5E0',
                    },
                  }}
                >
                  <Text fontSize="14px" fontWeight="500" color="gray.700">
                    Street
                  </Text>
                  <Text
                    h="20px"
                    color="#718096"
                    fontSize="14px"
                    whiteSpace={{ base: 'nowrap', md: 'unset' }}
                    isTruncated
                  >
                    {truncateWithEllipsis(propertyData.streetAddress, 18)}
                  </Text>
                </InfoStructureCard>
                <InfoStructureCard
                  tooltipTitle={propertyData.unitNo}
                  isLoading={isLoadingPropertyData && isLoadingStatesData}
                >
                  <Text fontSize="14px" fontWeight="500" color="gray.700">
                    Unit
                  </Text>
                  <Text h="20px" fontSize="14px" color="#718096" isTruncated noOfLines={1}>
                    {isValidAndNonEmpty(propertyData?.unitNo) ? truncateWithEllipsis(propertyData.unitNo, 10) : '---'}
                  </Text>
                </InfoStructureCard>
                <InfoStructureCard
                  tooltipTitle={propertyData.city}
                  isLoading={isLoadingPropertyData && isLoadingStatesData}
                >
                  <Text fontSize="14px" fontWeight="500" color="gray.700">
                    City
                  </Text>
                  <Text h="20px" fontSize="14px" color="#718096" isTruncated>
                    {truncateWithEllipsis(propertyData.city, 10)}
                  </Text>
                </InfoStructureCard>
                <InfoStructureCard
                  tooltipTitle={propertyData.state}
                  isLoading={isLoadingPropertyData && isLoadingStatesData}
                >
                  <Text fontSize="14px" fontWeight="500" color="gray.700">
                    State
                  </Text>
                  <Text h="20px" fontSize="14px" color="#718096" isTruncated>
                    {states.find(s => s.code === propertyData.state) &&
                      states.find(s => s.code === propertyData.state).name}
                  </Text>
                </InfoStructureCard>
                <InfoStructureCard
                  tooltipTitle={propertyData.zipCode}
                  isLoading={isLoadingPropertyData && isLoadingStatesData}
                >
                  <Text fontSize="14px" fontWeight="500" color="gray.700">
                    Zipcode
                  </Text>
                  <Text h="20px" fontSize="14px" color="#718096" isTruncated>
                    {truncateWithEllipsis(propertyData.zipCode, 10)}
                  </Text>
                </InfoStructureCard>
              </Flex>
            </Box>
            <Box mt="10px !important" mb="5px !important">
              <Divider orientation="horizontal" w="100%" borderColor="#CBD5E0" />
            </Box>

            <Box h="106px" mt="5px !important" w="100%">
              <HStack alignItems="flex-start" h="100%">
                <Box w="50%"  position="relative">
                  <HStack position="relative">
                    <Text color="gray.700" fontSize="14px" fontWeight="500" w="170px">
                      Resident Info
                    </Text>
                  </HStack>
                  {propertyData.residents?.length !== 1 && (<Box borderRadius="3px" w="25px" h="110px" bg="#EBF8FF" position="absolute" right="0" top="0" display="flex" alignItems={"center"} onClick={() => isLeftDirection ?  prevResident() : nextResident()} cursor={"pointer"}>
                    <Box
                      color="blue.100"
                      border="none"
                      display="flex"
                      alignItems={"center"}
                      bg="none"
                      _focus={{ border: 'none', bg: 'none' }}
                      _hover={{ border: 'none', bg: 'none' }}
                    >
                      <Box color={'#345EA6'}>
                        {isLeftDirection ? <BiCaretLeft size={25} /> : <BiCaretRight size={25} />}
                      </Box>
                    </Box>
                  </Box>)}
                  <Box>
                    <HStack mt="10px">
                      <Box w="30%">
                        <Stack>
                          <Text fontWeight="500" fontSize="14px" color="gray.700">
                            Name
                          </Text>
                          <Text fontWeight="400" fontSize="14px" color="gray.500" isTruncated>
                            {`${selectedResident?.firstName} ${selectedResident?.lastName}`}
                          </Text>
                        </Stack>
                      </Box>
                      <Box w="40%">
                        <Stack>
                          <Text fontWeight="500" fontSize="14px" color="gray.700">
                            Email
                          </Text>
                          <Text fontWeight="400" fontSize="14px" color="gray.500" isTruncated>
                            {selectedResident?.emailAddress}
                          </Text>
                        </Stack>
                      </Box>
                      <Box w="35%">
                        <Stack>
                          <Box pos="relative">
                            <Text fontWeight="500" fontSize="14px" color="gray.700">
                              Phone No
                            </Text>
                            <Box position="absolute" left="-40px" top="-12px">
                            {showCallModule &&
                              <IconButton
                                icon={<MdPhone width="11px" fontSize="14px" color="#345EA6" />}
                                aria-label="call resident"
                                maxW="32px"
                                minW="32px"
                                minH="32px"
                                maxH="32px"
                                borderRadius="full"
                                bg="#EBF8FF"
                                variant="unstyled"
                                display="flex"
                                justifyContent="center"
                                onClick={() => handleCall(selectedResident)}
                                zIndex="9"
                              />
                             }
                            </Box>
                          </Box>
                          <Text fontWeight="400" fontSize="14px" color="gray.500" isTruncated>
                            {selectedResident?.phoneNumber}
                          </Text>
                        </Stack>
                      </Box>
                    </HStack>
                  </Box>
                  {/* {propertyData.residents?.length !== 1 && (
                    <Box>
                      <HStack mt="10px" justifyContent="center">
                        <HStack
                          bgColor="#F2F3F4"
                          borderRadius="4px"
                          width="103px"
                          height="15px"
                          justifyContent="center"
                        >
                          <Text fontSize="12px" fontWeight="400">
                            <chakra.span color="gray.600">
                              {propertyData.residents?.indexOf(selectedResident) + 1}
                            </chakra.span>
                            <chakra.span color="#718096"> of </chakra.span>
                            <chakra.span color="#718096">{propertyData.residents?.length}</chakra.span>
                          </Text>
                          <Box>
                            <HStack>
                              <IconButton
                                aria-label={'Previous Resident'}
                                icon={<MdOutlineArrowBackIosNew />}
                                onClick={prevResident}
                                bg="transparent !important"
                                justifyContent="start"
                                minWidth="8px !important"
                                width="8px !important"
                                h="8px !important"
                                cursor="pointer"
                                color={
                                  propertyData?.residents?.findIndex(r => r.id === selectedResident?.id) === 0
                                    ? '#4A5568'
                                    : '#4A5568'
                                }
                              />
                              <IconButton
                                aria-label={'Next Resident'}
                                icon={<MdOutlineArrowForwardIos />}
                                onClick={nextResident}
                                bg="transparent !important"
                                justifyContent="start"
                                minWidth="8px !important"
                                width="8px !important"
                                h="8px !important"
                                cursor="pointer"
                                color={
                                  propertyData?.residents?.findIndex(r => r.id === selectedResident?.id) ===
                                  propertyData?.residents?.length - 1
                                    ? '#4A5568'
                                    : '#4A5568'
                                }
                              />
                            </HStack>
                          </Box>
                        </HStack>
                      </HStack>
                    </Box>
                  )} */}
                </Box>
                <Box position="relative" m="0" p="0">
                  <Box position="absolute" h="127px" top="-13px" mt="3px">
                    <Divider orientation="vertical" width="108px" borderColor="gray.300" />
                  </Box>
                </Box> 
                <Box w="50%" h="100%" position="relative" onMouseEnter={() => setIsClientBoxHovered(true)} onMouseLeave={() => setIsClientBoxHovered(false)} >
                  <HStack position="relative">
                    <Text color="gray.700" fontSize="14px" fontWeight="500" w="170px">
                      Client Info
                    </Text>
                  </HStack>
                  <Box w="90%">
                    <HStack mt="10px">
                      <Box w="30%">
                        <Stack>
                          <Text fontWeight="500" fontSize="14px" color="gray.700">
                            Name
                          </Text>
                          <Text fontWeight="400" fontSize="14px" color="gray.500" isTruncated>
                            {propertyData?.client?.companyName}
                          </Text>
                        </Stack>
                      </Box>
                      <Box w="40%">
                        <Stack>
                          <Text fontWeight="500" fontSize="14px" color="gray.700">
                            Email
                          </Text>
                          <Text fontWeight="400" fontSize="14px" color="gray.500" isTruncated>
                            {Array.isArray(propertyData?.client?.contacts) ? propertyData?.client?.contacts[0]?.emailAddress : "---"}
                          </Text>
                        </Stack>
                      </Box>
                      <Box w="35%">
                        <Stack>
                          <Box pos="relative">
                            <Text fontWeight="500" fontSize="14px" color="gray.700">
                              Phone No
                            </Text>
                          </Box>
                          <Text fontWeight="400" fontSize="14px" color="gray.500" isTruncated>
                            {Array.isArray(propertyData?.client?.contacts) ? propertyData?.client?.contacts[0]?.phoneNumber : "---"}
                          </Text>
                        </Stack>
                      </Box>
                    </HStack>
                  </Box>
                </Box>
              </HStack>
            </Box>
          </Stack>
        )}
      </Card>
      <UpdatePropertyModal
        isOpen={isOpenUpdatePropertyModal}
        onClose={onUpdatePropertyModalClose}
        propertyData={propertyData}
        projectId={projectId}
      />
      {isOpen && (
        <Client
          clientDetails={propertyData?.client}
          onClose={() => {
            onCloseDisclosure()
          }}
          isOpen={isOpen}
          propertyId={propertyId}
        />
      )}
    </>
  )
}

const InfoStructureCard: React.FC<{ isLoading: boolean; tooltipTitle: string } & CenterProps> = ({
  children,
  isLoading,
  title,
  tooltipTitle,
  ...rest
}) => {
  return (
    <HStack
      flex={rest.flex || 1}
      spacing={0}
      borderBottom={{ base: '1px solid #CBD5E0', md: 'none' }}
      _last={{ border: 'none' }}
    >
      <Center justifyContent={{ base: 'start', md: 'center' }} flex={rest.flex || 1} position="relative" {...rest}>
        <Box fontSize="14px" fontWeight={400} fontFamily="poppins">
          <Tooltip label={tooltipTitle} color="#fff" placement="top" fontWeight="500">
            <Text color="#000" whiteSpace={{ base: 'nowrap', md: 'unset' }} noOfLines={[0, 1]} fontWeight="400">
              {title}
            </Text>
          </Tooltip>
          {isLoading ? <BlankSlate size="sm" /> : children}
        </Box>
      </Center>
    </HStack>
  )
}
